import { MutationCache, QueryCache } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * A garantia de que **nenhuma requisição falha em silêncio**.
 *
 * O problema que isto resolve: cada `useMutation` decidia sozinho se avisava, e
 * a maioria não avisava. O resultado prático é o relato de quem opera —
 * "cliquei em salvar e não sei se foi". Um `onError` esquecido virava ação sem
 * resposta nenhuma: nem toast, nem console, nem inline.
 *
 * A regra, e o porquê de ela ser assimétrica entre leitura e escrita:
 *
 * - **Escrita (mutation) que falha → toast + `console.error`.** Escrita é ação
 *   que alguém disparou de propósito, esperando um efeito. Ficar sem resposta é
 *   o pior caso possível: a pessoa segue achando que salvou. O toast é global e
 *   aparece mesmo com o card/modal fora da área visível — numa prova de 80
 *   questões, a mensagem inline ao lado do campo pode estar a três telas de
 *   distância de onde a pessoa está olhando.
 * - **Leitura (query) que falha → só `console.error`.** A tela que depende da
 *   leitura já desenha o estado de erro dela (`ErrorState`, "Prova não
 *   encontrada"), e um toast por refetch de fundo seria ruído repetido dizendo
 *   o que a página já diz.
 *
 * **O console recebe o erro CRU**, não a mensagem tratada: é lá que estão o
 * status HTTP, o corpo da resposta e o stack — o que o toast não pode mostrar e
 * é exatamente o que se precisa para depurar. O objeto vai como segundo
 * argumento para o devtools deixar expandir em vez de imprimir `[object
 * Object]`.
 *
 * **Quem NÃO passa por aqui:** o que não é requisição (validação local de
 * formulário, leitura de zip, pré-flight de imagem) e o que roda pela fila de
 * requisições (`useRequestQueue`, usada na importação e nas ações em massa) —
 * a fila chama a função crua do repositório, não `useMutation`, e tem o próprio
 * painel de progresso/erro por linha, que é feedback melhor que um toast por
 * item. Nesses dois casos o aviso continua sendo de quem chama.
 *
 * Isto **não substitui** a mensagem inline ao lado do campo: ela diz ONDE
 * falhou e some junto com o formulário; o toast diz QUE falhou. Por isso o call
 * site não deve adicionar um segundo `toast.error` para a mesma requisição — aí
 * sim vira ruído duplicado.
 */
export function readApiErrorMessage(err: unknown, fallback: string): string {
  // Uma `string` passa direto: é a mensagem já traduzida por quem conhece o
  // código de erro do domínio.
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybe = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const apiMsg = maybe.response?.data?.message;
    if (typeof apiMsg === 'string') return apiMsg;
    if (Array.isArray(apiMsg) && typeof apiMsg[0] === 'string') {
      return apiMsg[0];
    }
    if (typeof maybe.message === 'string') return maybe.message;
  }
  return fallback;
}

/**
 * Um id de toast **por objeto de erro**, para a rede global e o call site não
 * empilharem dois avisos da mesma falha.
 *
 * O `onError` do cache roda ANTES do `onError` da própria mutation e antes do
 * `catch` de quem chamou `mutateAsync`, então não dá para o call site "avisar
 * que já avisou". Com o id derivado do erro, o segundo `toast.error` **atualiza
 * o primeiro em vez de somar** — e como o call site vem depois, é a mensagem
 * dele (a que tem contexto: "Falha ao publicar a prova") que fica na tela. Sem
 * call site, fica a da rede global, que é a mensagem da API.
 *
 * `WeakMap`: a chave é o próprio erro, que o React Query segura enquanto a
 * mutation existe e solta depois — nada a limpar.
 */
const toastIdByError = new WeakMap<object, string>();
let toastSeq = 0;

export function errorToastId(err: unknown): string | undefined {
  // Sem objeto não há identidade para casar (ex.: `notifyFailed(null, …)` de
  // validação local) — o sonner gera o id dele e o aviso aparece normalmente.
  if (!err || typeof err !== 'object') return undefined;
  const existing = toastIdByError.get(err);
  if (existing) return existing;
  const id = `request-error-${toastSeq++}`;
  toastIdByError.set(err, id);
  return id;
}

/**
 * Os caches que levam o `onError` global. São passados ao `QueryClient` na
 * construção (não dá para instalá-los depois), então isto devolve os dois
 * prontos para o spread em `new QueryClient({ ... })`.
 */
export function createRequestFeedbackCaches() {
  return {
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error(
          `[request] leitura falhou: ${JSON.stringify(query.queryKey)}`,
          error,
        );
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const key = mutation.options.mutationKey;
        console.error(
          `[request] escrita falhou${key ? `: ${JSON.stringify(key)}` : ''}`,
          error,
        );
        toast.error(
          readApiErrorMessage(
            error,
            'A ação não foi concluída. Tente de novo; se repetir, copie o erro do console (F12).',
          ),
          { id: errorToastId(error) },
        );
      },
    }),
  };
}
